import React from "react";
import {
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuSeparator,
  // Calendar,
  Button,
} from "@b2b/ui-components";
import {
  Search,
  Filter,
  ChevronDown,
  Download,
  RefreshCw,
  Plus,
} from "lucide-react";
// import { format } from "date-fns";

interface ToolbarState {
  q: string;
  status: string;
  country: string;
  from?: Date;
  to?: Date;
  pageSize: number;
}

interface DataToolbarProps {
  state: ToolbarState;
  setState: (p: Partial<ToolbarState>) => void;
  countries: string[];
  onExport: () => void;
  onReset: () => void;
  onCreate: () => void;
}

const DataToolbar: React.FC<DataToolbarProps> = ({
  state,
  setState,
  countries,
  onExport,
  onReset,
  onCreate,
}) => {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
      {/* Filters */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3 w-full">
        {/* Search */}
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-60" />
          <Input
            className="pl-9 w-full"
            placeholder="Search RFQ ID, title, company..."
            value={state.q}
            onChange={(e) => setState({ q: e.target.value })}
          />
        </div>

        {/* Status */}
        <Select
          value={state.status}
          onValueChange={(v) => setState({ status: v })}
        >
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent align="start" className="bg-white">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="quoted">Quoted</SelectItem>
            <SelectItem value="awarded">Awarded</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>

        {/* Country */}
        <Select
          value={state.country}
          onValueChange={(v) => setState({ country: v })}
        >
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="Country" />
          </SelectTrigger>
          <SelectContent align="start" className="bg-white">
            <SelectItem value="all">All Countries</SelectItem>
            {countries.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Date Range */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start sm:w-56">
              <Filter className="mr-2 h-4 w-4" />
              {state.from && state.to ? (
                <span>
                  {/* {format(state.from, "dd MMM")} – {format(state.to, "dd MMM yyyy")} */}
                </span>
              ) : (
                <span>Date range</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-3" align="start">
            <div className="grid gap-2">
              {/* Calendar here */}
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setState({ from: undefined, to: undefined })}
                >
                  Clear
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
        {/* Bulk Actions */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-full sm:w-auto">
              <ChevronDown className="mr-2 h-4 w-4" /> More
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52 bg-white">
            <DropdownMenuLabel>Bulk Actions</DropdownMenuLabel>
            <DropdownMenuItem onClick={onExport}>
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onReset}>
              <RefreshCw className="mr-2 h-4 w-4" /> Reset Filters
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Page Size */}
        <Select
          value={String(state.pageSize)}
          onValueChange={(v) => setState({ pageSize: Number(v) })}
        >
          <SelectTrigger className="w-full sm:w-[120px]">
            <SelectValue placeholder="Rows" />
          </SelectTrigger>
          <SelectContent align="end" className="bg-white">
            {[10, 25, 50, 100].map((n) => (
              <SelectItem key={n} value={String(n)}>
                Rows: {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Create RFQ - Only show if onCreate is provided (buyers only) */}
        {onCreate && (
          <Button
            onClick={onCreate}
            variant="outline"
            className="w-full sm:w-auto"
          >
            <Plus className="mr-2 h-4 w-4" /> Create RFQ
          </Button>
        )}
      </div>
    </div>
  );
};

export default DataToolbar;
